import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Check, MessageCircle, Mail, ExternalLink, Download, PlayCircle, FileWarning } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { Reveal } from "@/components/common/Reveal";
import SpecTable from "@/components/product/SpecTable";
import ProductCard from "@/components/product/ProductCard";
import EmptyState from "@/components/common/EmptyState";
import WhatsAppButton from "@/components/common/WhatsAppButton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { productsService } from "@/services/productsService";
import { brandName, categoryName, subcategoryName } from "@/lib/labels";
import { toEmbed } from "@/lib/videoEmbed";
import { useInquiryCart } from "@/context/InquiryCartContext";
import { useBrands, useSite, useWaLink } from "@/context/SiteDataContext";
import { toast } from "sonner";

/** Human-readable file size for the downloads list. */
const formatFileSize = (bytes) => {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
};

function Gallery({ images, name }) {
  const [active, setActive] = useState(0);
  return (
    <div className="lg:sticky lg:top-28">
      {/* object-contain, not cover: laboratory equipment is tall and narrow, and
          cropping it to a square cut the tops and bottoms off the products. The
          whole item has to be visible in a catalogue. */}
      <div className="relative overflow-hidden rounded-lg bg-[#f6f8f9] aspect-square group p-6">
        <motion.img key={active} initial={{ opacity: 0.4, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }} src={images[active]} alt={name} className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105" />
      </div>
      {images.length > 1 && (
        <div className="mt-4 flex gap-3">
          {images.map((img, i) => (
            <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`} className={`w-20 h-20 rounded-md overflow-hidden bg-[#f6f8f9] border-2 transition-all ${active === i ? "border-aqua" : "border-transparent hover:border-border"}`}>
              <img src={img} alt="" loading="lazy" decoding="async" className="w-full h-full object-contain p-1.5" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(undefined);
  const [related, setRelated] = useState(null);
  const [added, setAdded] = useState(false);
  const { addItem, has } = useInquiryCart();
  // Hooks must run before the loading / not-found early returns below.
  const brands = useBrands();
  const SITE = useSite();
  const waLink = useWaLink();

  useEffect(() => {
    setProduct(undefined);
    productsService.getProductBySlug(slug).then((p) => {
      setProduct(p);
      if (p) productsService.getRelatedProducts(p).then(setRelated);
    });
  }, [slug]);

  if (product === undefined) {
    return (
      <div className="container-x py-14 grid lg:grid-cols-2 gap-12">
        <Skeleton className="aspect-square w-full rounded-lg" />
        <div className="space-y-4"><Skeleton className="h-4 w-24" /><Skeleton className="h-10 w-3/4" /><Skeleton className="h-20 w-full" /><Skeleton className="h-12 w-full" /></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container-x py-24">
        <EmptyState icon={FileWarning} title="Product not found" description="The product you are looking for is unavailable or may have been moved." actionLabel="Back to Products" actionTo="/products" />
      </div>
    );
  }

  const brand = brands.find((b) => b.slug === product.brand);
  const inCart = has(product.id);
  const waMsg = `Hi Athena Scientific, I would like to enquire about ${product.name}.`;

  const handleAdd = () => {
    addItem({ productId: product.id, name: product.name, brand: product.brand, category: product.category, slug: product.slug, image: product.images?.[0] });
    setAdded(true);
    setTimeout(() => setAdded(false), 1600);
    toast.success("Added to Inquiry Cart", { description: product.name });
  };

  const crumbs = [
    { label: "Products", to: "/products" },
    { label: categoryName(product.category), to: `/products?category=${product.category}` },
    { label: product.name },
  ];

  return (
    <>
      <Seo
        title={product.seo?.title || product.name}
        description={
          product.seo?.description ||
          `${product.name} by ${brandName(product.brand)} — ${categoryName(product.category)}. Enquire with Athena Scientific for specifications and support.`
        }
        ogImage={product.seo?.ogImage || product.images?.[0]}
        canonical={`https://${SITE.website}/products/${product.slug}`}
        jsonLd={{ "@context": "https://schema.org", "@type": "Product", name: product.name, brand: { "@type": "Brand", name: brandName(product.brand) }, category: categoryName(product.category), image: product.images }}
      />
      <div className="container-x pt-8">
        <Breadcrumbs crumbs={crumbs} />
      </div>

      <div className="container-x py-10 grid lg:grid-cols-2 gap-10 lg:gap-16">
        <Gallery images={product.images} name={product.name} />

        <div>
          <Reveal>
            <Link to={`/products?brand=${product.brand}`} className="text-sm font-bold uppercase tracking-widest text-aqua hover:underline">{brandName(product.brand)}</Link>
            <h1 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-ink">{product.name}</h1>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-xs font-semibold px-3 py-1.5 rounded-md bg-[#f6f8f9] text-greyish">{categoryName(product.category)}</span>
              {product.subcategory && <span className="text-xs font-semibold px-3 py-1.5 rounded-md bg-[#f6f8f9] text-greyish">{subcategoryName(product.subcategory)}</span>}
            </div>
            <p className="mt-6 text-greyish text-lg leading-relaxed">{product.shortDescription}</p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={handleAdd} data-testid="detail-add-to-inquiry" className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md font-semibold transition-all ${added || inCart ? "bg-aqua text-white" : "bg-ink text-white hover:bg-aqua"}`}>
                {added ? <Check className="w-5 h-5" /> : <Plus className="w-5 h-5" />} {inCart ? "In Inquiry Cart" : "Add to Inquiry"}
              </button>
              <Link to="/contact" data-testid="detail-contact" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md font-semibold border border-border text-ink hover:bg-[#f6f8f9] transition-all">
                <Mail className="w-5 h-5" /> Contact Us
              </Link>
            </div>
            <a href={waLink(waMsg)} target="_blank" rel="noopener noreferrer" data-testid="detail-whatsapp" className="mt-3 inline-flex items-center gap-2 text-[#1EBE5A] font-semibold hover:text-[#25D366] transition-colors">
              <MessageCircle className="w-5 h-5" /> Enquire about this product on WhatsApp
            </a>

            <p className="mt-6 text-xs text-greyish bg-[#f9fafb] border border-border rounded-md px-4 py-3">
              This is a product enquiry, not an online purchase. Add items to your inquiry cart and our team will follow up with details and a tailored recommendation.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="container-x pb-10">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="w-full justify-start overflow-x-auto h-auto flex-wrap bg-transparent border-b border-border rounded-none p-0 gap-1">
            {["overview", "specifications", "downloads", "videos"].map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="capitalize rounded-none border-b-2 border-transparent data-[state=active]:border-aqua data-[state=active]:text-aqua data-[state=active]:shadow-none px-5 py-3 font-semibold">{t}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="pt-8 max-w-3xl">
            <h2 className="text-2xl font-bold text-ink">Overview</h2>
            <p className="mt-4 text-greyish leading-relaxed">{product.description}</p>
          </TabsContent>

          <TabsContent value="specifications" className="pt-8 max-w-3xl">
            <h2 className="text-2xl font-bold text-ink mb-6">Specifications</h2>
            <SpecTable specs={product.specifications} />
          </TabsContent>

          <TabsContent value="downloads" className="pt-8 max-w-3xl">
            <h2 className="text-2xl font-bold text-ink mb-6">Downloads</h2>
            <div className="space-y-3">
              {product.documents.map((d) => {
                // Every document uploaded through the CMS carries a real
                // download URL. The row used to be a plain <div> ending in a
                // static "Request" label, so clicking a datasheet did
                // nothing at all — the file was there, the link was not.
                // Documents added before the uploader existed have no url,
                // and those genuinely are available on request.
                const href = d.url || null;
                const Row = href ? "a" : "div";
                const rowProps = href
                  ? {
                      href,
                      target: "_blank",
                      rel: "noopener noreferrer",
                      // Suggest the document's own title rather than the
                      // timestamped storage filename.
                      download: d.title || true,
                    }
                  : {};
                return (
                  <Row
                    key={d.id}
                    {...rowProps}
                    data-testid={`download-${d.id}`}
                    className={`flex items-center justify-between p-4 border border-border rounded-md transition-all ${
                      href ? "hover:border-aqua/40 hover:bg-[#f6f8f9] cursor-pointer" : "opacity-90"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded bg-aqua/10 flex items-center justify-center shrink-0">
                        <Download className="w-5 h-5 text-aqua" />
                      </div>
                      <div>
                        <p className="font-semibold text-ink text-sm">{d.title}</p>
                        <p className="text-xs text-greyish">
                          {d.type}
                          {d.size ? ` · ${formatFileSize(d.size)}` : ""}
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs font-semibold ${href ? "text-aqua" : "text-greyish"}`}>
                      {href ? "Download" : "Available on request"}
                    </span>
                  </Row>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="videos" className="pt-8 max-w-3xl">
            <h2 className="text-2xl font-bold text-ink mb-6">Videos</h2>
            {product.videos.length === 0 ? (
              <div className="border border-dashed border-border rounded-lg p-10 text-center">
                <PlayCircle className="w-8 h-8 text-greyish mx-auto mb-3" strokeWidth={1.5} />
                <p className="font-semibold text-ink">No videos yet</p>
                <p className="mt-1 text-sm text-greyish">Product videos will be added here when available.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {product.videos.map((v, i) => {
                  const embed = toEmbed(v);

                  // A link we cannot embed becomes a proper link rather than an
                  // empty frame — YouTube refuses to render "watch" URLs inside
                  // an iframe, which is what produced the blank grey box.
                  if (!embed) {
                    return (
                      <a
                        key={v}
                        href={v}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aspect-video rounded-lg border border-border bg-[#f6f8f9] flex flex-col items-center justify-center gap-2 text-center p-6 hover:border-aqua/50 transition-colors"
                      >
                        <PlayCircle className="w-8 h-8 text-aqua" strokeWidth={1.5} />
                        <span className="text-sm font-semibold text-ink">Watch video</span>
                        <span className="text-xs text-greyish break-all line-clamp-2">{v}</span>
                      </a>
                    );
                  }

                  if (embed.type === "file") {
                    return (
                      <div key={v} className="aspect-video rounded-lg overflow-hidden bg-black">
                        <video src={embed.src} controls className="w-full h-full" />
                      </div>
                    );
                  }

                  return (
                    <div key={v} className="aspect-video rounded-lg overflow-hidden bg-black">
                      <iframe
                        src={embed.src}
                        title={`${product.name} video ${i + 1}`}
                        className="w-full h-full"
                        loading="lazy"
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {(product.externalUrl || brand?.website) && (
          <div className="mt-8 max-w-3xl">
            <a href={product.externalUrl || brand.website} target="_blank" rel="noopener noreferrer" data-testid="detail-external-link" className="inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-aqua transition-colors">
              Visit manufacturer website ({brandName(product.brand)}) <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>

      <section className="bg-[#f9fafb] border-t border-border">
        <div className="container-x py-16 md:py-20">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-ink mb-8">Related Products</h2>
          {!related ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-lg" />)}</div>
          ) : related.length === 0 ? (
            <p className="text-greyish">No related products found.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
          )}
        </div>
      </section>
    </>
  );
}
